import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { 
  Users, TrendingUp, Clock, MessageCircle, Zap, 
  ArrowLeft, RefreshCw, DollarSign, AlertTriangle,
  BarChart3, Target, Flame, Crown, Activity, ShieldX,
  Heart, Brain
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const AnalyticsDashboard = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [accessDenied, setAccessDenied] = useState(false);
  
  // Data states
  const [globalMetrics, setGlobalMetrics] = useState(null);
  const [retention, setRetention] = useState(null);
  const [engagement, setEngagement] = useState(null);
  const [funnel, setFunnel] = useState(null);
  const [behavior, setBehavior] = useState(null);
  const [churnRisk, setChurnRisk] = useState(null);
  const [costs, setCosts] = useState(null);
  
  // Daily Companion metrics
  const [companionHealth, setCompanionHealth] = useState(null);
  const [relationshipOverview, setRelationshipOverview] = useState(null);
  const [continuityMetrics, setContinuityMetrics] = useState(null);
  const [memoryUsage, setMemoryUsage] = useState(null);
  const [conversationQuality, setConversationQuality] = useState(null);

  useEffect(() => {
    // Check admin access on mount
    if (user && user.role !== 'admin') {
      setAccessDenied(true);
      setLoading(false);
      return;
    }
    fetchAllMetrics();
  }, [user]);

  const fetchAllMetrics = async () => {
    setRefreshing(true);
    const token = localStorage.getItem('token');
    const headers = { Authorization: `Bearer ${token}` };

    try {
      // Use new admin routes
      const [globalRes, retentionRes, engagementRes, funnelRes, behaviorRes, churnRes, costsRes,
             healthRes, overviewRes, continuityRes, memoryRes, qualityRes] = await Promise.all([
        fetch(`${API}/api/analytics/global`, { headers }).catch(() => ({ ok: false })),
        fetch(`${API}/api/admin/retention`, { headers }).catch(() => ({ ok: false, status: 403 })),
        fetch(`${API}/api/admin/engagement`, { headers }).catch(() => ({ ok: false, status: 403 })),
        fetch(`${API}/api/admin/funnel`, { headers }).catch(() => ({ ok: false, status: 403 })),
        fetch(`${API}/api/admin/behavior`, { headers }).catch(() => ({ ok: false, status: 403 })),
        fetch(`${API}/api/admin/churn-risk`, { headers }).catch(() => ({ ok: false, status: 403 })),
        fetch(`${API}/api/admin/costs`, { headers }).catch(() => ({ ok: false, status: 403 })),
        // Daily Companion metrics
        fetch(`${API}/api/admin/relationship/health`, { headers }).catch(() => ({ ok: false })),
        fetch(`${API}/api/admin/relationship/overview?days=30`, { headers }).catch(() => ({ ok: false })),
        fetch(`${API}/api/admin/relationship/continuity?days=14`, { headers }).catch(() => ({ ok: false })),
        fetch(`${API}/api/admin/relationship/memory-usage`, { headers }).catch(() => ({ ok: false })),
        fetch(`${API}/api/admin/relationship/conversation-quality?days=14`, { headers }).catch(() => ({ ok: false })),
      ]);

      // Check if any response is 403 (unauthorized)
      if (retentionRes.status === 403 || engagementRes.status === 403) {
        setAccessDenied(true);
        setLoading(false);
        return;
      }

      if (globalRes.ok) setGlobalMetrics(await globalRes.json());
      if (retentionRes.ok) setRetention(await retentionRes.json());
      if (engagementRes.ok) setEngagement(await engagementRes.json());
      if (funnelRes.ok) setFunnel(await funnelRes.json());
      if (behaviorRes.ok) setBehavior(await behaviorRes.json());
      if (churnRes.ok) setChurnRisk(await churnRes.json());
      if (costsRes.ok) setCosts(await costsRes.json());
      
      // Daily Companion metrics
      if (healthRes.ok) setCompanionHealth(await healthRes.json());
      if (overviewRes.ok) setRelationshipOverview(await overviewRes.json());
      if (continuityRes.ok) setContinuityMetrics(await continuityRes.json());
      if (memoryRes.ok) setMemoryUsage(await memoryRes.json());
      if (qualityRes.ok) setConversationQuality(await qualityRes.json());

    } catch (error) {
      toast.error('Erro ao carregar métricas');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  // Access Denied Screen
  if (accessDenied) {
    return (
      <div className="min-h-screen bg-sand flex items-center justify-center px-4">
        <motion.div 
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="max-w-md w-full text-center"
        >
          <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <ShieldX className="w-10 h-10 text-red-500" />
          </div>
          
          <h1 className="font-fraunces text-2xl font-semibold text-forest mb-3">
            Acesso Restrito
          </h1>
          
          <p className="text-stone mb-8">
            Esta área é reservada para administradores do sistema.
          </p>
          
          <Button
            onClick={() => navigate('/dashboard')}
            className="w-full bg-forest text-sand hover:bg-forest/90 rounded-full py-6"
            data-testid="back-to-dashboard-btn"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Voltar ao Início
          </Button>
        </motion.div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-sand flex items-center justify-center">
        <div className="text-center">
          <div className="w-10 h-10 border-3 border-forest/20 border-t-forest rounded-full animate-spin mx-auto mb-4" />
          <p className="text-forest">Carregando analytics...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-sand" data-testid="analytics-dashboard">
      {/* Header */}
      <header className="bg-white border-b border-forest/10 sticky top-0 z-10">
        <div className="px-6 py-4 flex justify-between items-center max-w-7xl mx-auto">
          <div className="flex items-center gap-4">
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => navigate('/dashboard')}
              className="text-stone"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Voltar
            </Button>
            <div>
              <h1 className="font-fraunces text-xl font-semibold text-forest">Analytics Dashboard</h1>
              <p className="text-xs text-stone">Métricas e Inteligência de Retenção</p>
            </div>
          </div>
          <Button 
            onClick={fetchAllMetrics}
            disabled={refreshing}
            variant="outline"
            className="text-forest border-forest/20"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
            Atualizar
          </Button>
        </div>
      </header>

      {/* Main Content */}
      <main className="p-6 max-w-7xl mx-auto space-y-6">
        
        {/* Key Metrics Row */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <MetricCard 
            icon={<Users className="w-5 h-5" />}
            label="Total Usuários"
            value={globalMetrics?.total_users || 0}
            color="blue"
          />
          <MetricCard 
            icon={<Crown className="w-5 h-5" />}
            label="Premium"
            value={globalMetrics?.premium_users || 0}
            subtext={`${globalMetrics?.conversion_rate_percent || 0}% conversão`}
            color="yellow"
          />
          <MetricCard 
            icon={<Activity className="w-5 h-5" />}
            label="DAU/MAU"
            value={`${engagement?.dau || 0}/${engagement?.mau || 0}`}
            subtext={`${engagement?.stickiness_percent || 0}% stickiness`}
            color="green"
          />
          <MetricCard 
            icon={<Clock className="w-5 h-5" />}
            label="Tempo Médio"
            value={`${engagement?.avg_time_in_app_minutes || 0} min`}
            color="purple"
          />
        </div>

        {/* Retention Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-2xl p-6 shadow-soft"
        >
          <div className="flex items-center gap-2 mb-6">
            <TrendingUp className="w-5 h-5 text-forest" />
            <h2 className="font-semibold text-forest text-lg">Retenção</h2>
          </div>
          
          <div className="grid grid-cols-3 gap-6">
            <RetentionCard label="D1" value={retention?.d1_retention || 0} benchmark={40} />
            <RetentionCard label="D7" value={retention?.d7_retention || 0} benchmark={20} />
            <RetentionCard label="D30" value={retention?.d30_retention || 0} benchmark={10} />
          </div>

          {/* Retention Curve */}
          {retention?.retention_curve && (
            <div className="mt-6 pt-6 border-t border-forest/10">
              <h3 className="text-sm font-medium text-stone mb-4">Curva de Retenção</h3>
              <div className="flex items-end gap-1 h-24">
                {retention.retention_curve.slice(0, 14).map((point, i) => (
                  <div 
                    key={i}
                    className="flex-1 bg-lime/60 hover:bg-lime transition-colors rounded-t"
                    style={{ height: `${Math.max(point.rate, 5)}%` }}
                    title={`D${point.day}: ${point.rate}%`}
                  />
                ))}
              </div>
              <div className="flex justify-between text-xs text-stone/60 mt-1">
                <span>D1</span>
                <span>D7</span>
                <span>D14</span>
              </div>
            </div>
          )}
        </motion.div>

        {/* Two Column Layout */}
        <div className="grid lg:grid-cols-2 gap-6">
          
          {/* Funnel */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-white rounded-2xl p-6 shadow-soft"
          >
            <div className="flex items-center gap-2 mb-6">
              <Target className="w-5 h-5 text-forest" />
              <h2 className="font-semibold text-forest text-lg">Funil de Conversão</h2>
            </div>
            
            {funnel?.funnel && (
              <div className="space-y-4">
                <FunnelStep 
                  label="Usuários Cadastrados" 
                  value={funnel.funnel.total_users}
                  percent={100}
                />
                <FunnelStep 
                  label="Onboarding Completo" 
                  value={funnel.funnel.completed_onboarding}
                  percent={Math.round((funnel.funnel.completed_onboarding / funnel.funnel.total_users) * 100) || 0}
                />
                <FunnelStep 
                  label="Primeira Conversa" 
                  value={funnel.funnel.had_first_chat}
                  percent={Math.round((funnel.funnel.had_first_chat / funnel.funnel.total_users) * 100) || 0}
                />
                <FunnelStep 
                  label="Premium" 
                  value={funnel.funnel.became_premium}
                  percent={Math.round((funnel.funnel.became_premium / funnel.funnel.total_users) * 100) || 0}
                  highlight
                />
              </div>
            )}

            <div className="mt-6 pt-4 border-t border-forest/10 grid grid-cols-2 gap-4 text-center">
              <div>
                <p className="text-2xl font-bold text-forest">{funnel?.rates?.onboarding_completion_rate || 0}%</p>
                <p className="text-xs text-stone">Taxa Onboarding</p>
              </div>
              <div>
                <p className="text-2xl font-bold text-forest">{funnel?.rates?.free_to_premium_rate || 0}%</p>
                <p className="text-xs text-stone">Free → Premium</p>
              </div>
            </div>
          </motion.div>

          {/* Churn Risk */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-white rounded-2xl p-6 shadow-soft"
          >
            <div className="flex items-center gap-2 mb-6">
              <AlertTriangle className="w-5 h-5 text-orange-500" />
              <h2 className="font-semibold text-forest text-lg">Risco de Churn</h2>
            </div>
            
            <div className="grid grid-cols-2 gap-4 mb-6">
              <div className="bg-red-50 rounded-xl p-4 text-center">
                <p className="text-3xl font-bold text-red-600">{churnRisk?.high_risk_count || 0}</p>
                <p className="text-xs text-red-600/80">Alto Risco (7-14 dias)</p>
              </div>
              <div className="bg-orange-50 rounded-xl p-4 text-center">
                <p className="text-3xl font-bold text-orange-600">{churnRisk?.medium_risk_count || 0}</p>
                <p className="text-xs text-orange-600/80">Médio Risco (3-7 dias)</p>
              </div>
            </div>

            {churnRisk?.high_risk_users?.length > 0 && (
              <div>
                <h3 className="text-sm font-medium text-stone mb-3">Usuários Inativos (Alto Risco)</h3>
                <div className="space-y-2 max-h-40 overflow-y-auto">
                  {churnRisk.high_risk_users.slice(0, 5).map((user, i) => (
                    <div key={i} className="flex justify-between items-center text-sm py-2 border-b border-forest/5">
                      <span className="text-forest">{user.name}</span>
                      <span className="text-stone">{user.days_inactive} dias</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </motion.div>
        </div>

        {/* Behavior & Costs Row */}
        <div className="grid lg:grid-cols-2 gap-6">
          
          {/* Popular Features */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="bg-white rounded-2xl p-6 shadow-soft"
          >
            <div className="flex items-center gap-2 mb-6">
              <BarChart3 className="w-5 h-5 text-forest" />
              <h2 className="font-semibold text-forest text-lg">Comportamento</h2>
            </div>
            
            {behavior?.feature_usage_7d && (
              <div className="space-y-3">
                <h3 className="text-sm font-medium text-stone">Uso de Features (7 dias)</h3>
                {Object.entries(behavior.feature_usage_7d)
                  .sort(([,a], [,b]) => b - a)
                  .map(([feature, count], i) => (
                    <div key={i} className="flex justify-between items-center">
                      <span className="text-sm text-forest capitalize">{feature.replace(/_/g, ' ')}</span>
                      <span className="text-sm font-medium text-forest">{count}</span>
                    </div>
                  ))
                }
              </div>
            )}

            {behavior?.popular_topics?.length > 0 && (
              <div className="mt-6 pt-4 border-t border-forest/10">
                <h3 className="text-sm font-medium text-stone mb-3">Tópicos Populares</h3>
                <div className="flex flex-wrap gap-2">
                  {behavior.popular_topics.map((topic, i) => (
                    <span key={i} className="px-3 py-1 bg-lime/30 text-forest text-sm rounded-full">
                      {topic.topic} ({topic.count})
                    </span>
                  ))}
                </div>
              </div>
            )}
          </motion.div>

          {/* Costs */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="bg-white rounded-2xl p-6 shadow-soft"
          >
            <div className="flex items-center gap-2 mb-6">
              <DollarSign className="w-5 h-5 text-forest" />
              <h2 className="font-semibold text-forest text-lg">Custos & Receita</h2>
            </div>
            
            {costs && (
              <>
                <div className="grid grid-cols-2 gap-4 mb-6">
                  <div className="bg-green-50 rounded-xl p-4 text-center">
                    <p className="text-2xl font-bold text-green-600">
                      R$ {costs.revenue_estimate?.estimated_mrr_brl?.toLocaleString() || 0}
                    </p>
                    <p className="text-xs text-green-600/80">MRR Estimado</p>
                  </div>
                  <div className="bg-red-50 rounded-xl p-4 text-center">
                    <p className="text-2xl font-bold text-red-600">
                      $ {costs.estimated_costs_30d_usd?.total || 0}
                    </p>
                    <p className="text-xs text-red-600/80">Custo 30d (USD)</p>
                  </div>
                </div>

                <div className="space-y-3">
                  <div className="flex justify-between text-sm">
                    <span className="text-stone">Mensagens IA (30d)</span>
                    <span className="font-medium text-forest">{costs.usage_30d?.ai_messages?.toLocaleString() || 0}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-stone">TTS Premium (30d)</span>
                    <span className="font-medium text-forest">{costs.usage_30d?.tts_calls?.toLocaleString() || 0}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-stone">Custo/Usuário Premium</span>
                    <span className="font-medium text-forest">$ {costs.unit_economics?.cost_per_premium_user_30d || 0}</span>
                  </div>
                </div>
              </>
            )}
          </motion.div>
        </div>

        {/* Engagement Details */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="bg-white rounded-2xl p-6 shadow-soft"
        >
          <div className="flex items-center gap-2 mb-6">
            <Zap className="w-5 h-5 text-forest" />
            <h2 className="font-semibold text-forest text-lg">Engajamento Detalhado</h2>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            <div className="text-center">
              <p className="text-3xl font-bold text-forest">{engagement?.avg_sessions_per_user_7d || 0}</p>
              <p className="text-xs text-stone">Sessões/Usuário (7d)</p>
            </div>
            <div className="text-center">
              <p className="text-3xl font-bold text-forest">{engagement?.messages_per_session || 0}</p>
              <p className="text-xs text-stone">Mensagens/Sessão</p>
            </div>
            <div className="text-center">
              <p className="text-3xl font-bold text-forest">{engagement?.voice_usage_rate_percent || 0}%</p>
              <p className="text-xs text-stone">Taxa de Uso de Voz</p>
            </div>
            <div className="text-center">
              <p className="text-3xl font-bold text-forest">{engagement?.total_sessions_30d || 0}</p>
              <p className="text-xs text-stone">Sessões Totais (30d)</p>
            </div>
          </div>
        </motion.div>

        {/* ===== DAILY COMPANION SECTION ===== */}
        <div className="pt-8 border-t border-forest/10">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 bg-gradient-to-br from-pink-400 to-purple-500 rounded-xl flex items-center justify-center">
              <Heart className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2 className="font-fraunces text-xl font-semibold text-forest">Daily Companion</h2>
              <p className="text-xs text-stone">Métricas de Relacionamento com a Lira</p>
            </div>
          </div>

          {/* Companion Health Score */}
          {companionHealth && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className={`rounded-2xl p-6 shadow-soft mb-6 ${
                companionHealth.status === 'healthy' ? 'bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200' :
                companionHealth.status === 'moderate' ? 'bg-gradient-to-r from-yellow-50 to-amber-50 border border-yellow-200' :
                'bg-gradient-to-r from-red-50 to-orange-50 border border-red-200'
              }`}
            >
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="font-semibold text-forest text-lg">Health Score</h3>
                  <p className="text-sm text-stone">{companionHealth.status_message}</p>
                </div>
                <div className={`text-5xl font-bold ${
                  companionHealth.status === 'healthy' ? 'text-green-600' :
                  companionHealth.status === 'moderate' ? 'text-yellow-600' :
                  'text-red-600'
                }`}>
                  {companionHealth.health_score}
                </div>
              </div>
              
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                <div className="bg-white/60 rounded-xl p-3 text-center">
                  <p className="text-2xl font-bold text-forest">{companionHealth.metrics?.dau_rate_percent || 0}%</p>
                  <p className="text-xs text-stone">DAU Rate</p>
                </div>
                <div className="bg-white/60 rounded-xl p-3 text-center">
                  <p className="text-2xl font-bold text-forest">{companionHealth.metrics?.avg_streak_days || 0}</p>
                  <p className="text-xs text-stone">Avg Streak</p>
                </div>
                <div className="bg-white/60 rounded-xl p-3 text-center">
                  <p className="text-2xl font-bold text-forest">{companionHealth.metrics?.best_friends_count || 0}</p>
                  <p className="text-xs text-stone">Best Friends</p>
                </div>
                <div className="bg-white/60 rounded-xl p-3 text-center">
                  <p className="text-2xl font-bold text-forest">{companionHealth.metrics?.active_memories || 0}</p>
                  <p className="text-xs text-stone">Active Memories</p>
                </div>
              </div>

              {companionHealth.recommendations?.length > 0 && (
                <div className="bg-white/80 rounded-xl p-4">
                  <p className="text-sm font-medium text-forest mb-2">Recomendações</p>
                  <ul className="space-y-1">
                    {companionHealth.recommendations.map((rec, i) => (
                      <li key={i} className="text-sm text-stone flex items-start gap-2">
                        <span className="text-amber-500">→</span>
                        {rec}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </motion.div>
          )}

          {/* Relationship Overview & Continuity Row */}
          <div className="grid md:grid-cols-2 gap-6 mb-6">
            {/* Relationship Levels */}
            {relationshipOverview && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-white rounded-2xl p-6 shadow-soft"
              >
                <div className="flex items-center gap-2 mb-4">
                  <Heart className="w-5 h-5 text-pink-500" />
                  <h3 className="font-semibold text-forest">Níveis de Relacionamento</h3>
                </div>
                
                <div className="space-y-3">
                  {['best_friend', 'close_friend', 'friend', 'acquaintance', 'new'].map((level) => {
                    const count = relationshipOverview.relationship_levels?.[level] || 0;
                    const total = relationshipOverview.total_users_with_relationships || 1;
                    const percent = Math.round((count / total) * 100);
                    const labels = {
                      best_friend: '💛 Best Friends',
                      close_friend: '💫 Close Friends', 
                      friend: '🤝 Friends',
                      acquaintance: '🌱 Acquaintances',
                      new: '👋 New'
                    };
                    return (
                      <div key={level}>
                        <div className="flex justify-between text-sm mb-1">
                          <span className="text-stone">{labels[level]}</span>
                          <span className="font-medium text-forest">{count}</span>
                        </div>
                        <div className="h-2 bg-forest/10 rounded-full overflow-hidden">
                          <div 
                            className="h-full bg-gradient-to-r from-pink-400 to-purple-500 rounded-full"
                            style={{ width: `${percent}%` }}
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>

                <div className="mt-4 pt-4 border-t border-forest/10 grid grid-cols-2 gap-4 text-center">
                  <div>
                    <p className="text-2xl font-bold text-forest">{relationshipOverview.totals?.hours || 0}h</p>
                    <p className="text-xs text-stone">Total Conversação</p>
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-forest">{relationshipOverview.active_users_in_period || 0}</p>
                    <p className="text-xs text-stone">Usuários Ativos (30d)</p>
                  </div>
                </div>
              </motion.div>
            )}

            {/* Continuity & Streaks */}
            {continuityMetrics && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-white rounded-2xl p-6 shadow-soft"
              >
                <div className="flex items-center gap-2 mb-4">
                  <Flame className="w-5 h-5 text-orange-500" />
                  <h3 className="font-semibold text-forest">Continuidade & Streaks</h3>
                </div>
                
                <div className="grid grid-cols-2 gap-4 mb-4">
                  <div className="bg-lime/10 rounded-xl p-4 text-center">
                    <p className="text-3xl font-bold text-forest">{continuityMetrics.continuity?.rate_percent || 0}%</p>
                    <p className="text-xs text-stone">Taxa de Continuidade</p>
                    <p className="text-xs text-stone/60">Conversas que retomam contexto</p>
                  </div>
                  <div className="bg-orange-50 rounded-xl p-4 text-center">
                    <p className="text-3xl font-bold text-orange-600">{continuityMetrics.streaks?.highest_streak || 0}</p>
                    <p className="text-xs text-stone">Maior Streak</p>
                    <p className="text-xs text-stone/60">Média: {continuityMetrics.streaks?.average_max_streak || 0} dias</p>
                  </div>
                </div>

                <div>
                  <p className="text-sm font-medium text-stone mb-2">Distribuição de Streaks</p>
                  <div className="flex gap-1">
                    {Object.entries(continuityMetrics.streaks?.distribution || {}).map(([range, count]) => (
                      <div 
                        key={range}
                        className="flex-1 bg-orange-100 rounded p-2 text-center"
                        title={`${range}: ${count} usuários`}
                      >
                        <p className="text-lg font-bold text-orange-600">{count}</p>
                        <p className="text-xs text-stone truncate">{range}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </motion.div>
            )}
          </div>

          {/* Memory Usage & Conversation Quality Row */}
          <div className="grid md:grid-cols-2 gap-6">
            {/* Memory Usage */}
            {memoryUsage && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-white rounded-2xl p-6 shadow-soft"
              >
                <div className="flex items-center gap-2 mb-4">
                  <Brain className="w-5 h-5 text-purple-500" />
                  <h3 className="font-semibold text-forest">Uso de Memória</h3>
                </div>
                
                <div className="grid grid-cols-3 gap-4 mb-4 text-center">
                  <div>
                    <p className="text-2xl font-bold text-forest">{memoryUsage.total_memories || 0}</p>
                    <p className="text-xs text-stone">Total Memórias</p>
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-forest">{memoryUsage.unique_users || 0}</p>
                    <p className="text-xs text-stone">Usuários</p>
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-forest">{memoryUsage.memories_per_user_avg || 0}</p>
                    <p className="text-xs text-stone">Avg/Usuário</p>
                  </div>
                </div>

                <div>
                  <p className="text-sm font-medium text-stone mb-2">Por Categoria</p>
                  <div className="space-y-2">
                    {Object.entries(memoryUsage.categories || {}).slice(0, 5).map(([cat, data]) => (
                      <div key={cat} className="flex items-center justify-between text-sm">
                        <span className="text-stone capitalize">{cat}</span>
                        <span className="font-medium text-forest">{data.count}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </motion.div>
            )}

            {/* Conversation Quality */}
            {conversationQuality && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-white rounded-2xl p-6 shadow-soft"
              >
                <div className="flex items-center gap-2 mb-4">
                  <MessageCircle className="w-5 h-5 text-blue-500" />
                  <h3 className="font-semibold text-forest">Qualidade das Conversas</h3>
                </div>
                
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <p className="text-sm text-stone">Engagement Score</p>
                    <p className="text-xs text-stone/60">{conversationQuality.total_sessions || 0} sessões analisadas</p>
                  </div>
                  <div className={`text-4xl font-bold ${
                    conversationQuality.engagement_score >= 70 ? 'text-green-600' :
                    conversationQuality.engagement_score >= 50 ? 'text-yellow-600' :
                    'text-red-600'
                  }`}>
                    {conversationQuality.engagement_score || 0}
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3 mb-4">
                  <div className="bg-blue-50 rounded-lg p-3 text-center">
                    <p className="text-xl font-bold text-blue-600">{conversationQuality.averages?.duration_minutes || 0} min</p>
                    <p className="text-xs text-stone">Duração Média</p>
                  </div>
                  <div className="bg-purple-50 rounded-lg p-3 text-center">
                    <p className="text-xl font-bold text-purple-600">{conversationQuality.averages?.vocabulary_per_session || 0}</p>
                    <p className="text-xs text-stone">Vocabulário/Sessão</p>
                  </div>
                </div>

                {conversationQuality.emotional_analysis && (
                  <div>
                    <p className="text-sm font-medium text-stone mb-2">Tom Emocional</p>
                    <div className="flex gap-2">
                      <span className="px-2 py-1 bg-green-100 text-green-700 rounded text-xs">
                        😊 Positivo: {conversationQuality.emotional_analysis.positive_rate_percent || 0}%
                      </span>
                      {Object.entries(conversationQuality.emotional_analysis.distribution || {}).slice(0, 3).map(([tone, count]) => (
                        <span key={tone} className="px-2 py-1 bg-gray-100 text-gray-600 rounded text-xs">
                          {tone}: {count}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </motion.div>
            )}
          </div>
        </div>
        {/* ===== END DAILY COMPANION SECTION ===== */}

      </main>
    </div>
  );
};

// Helper Components
const MetricCard = ({ icon, label, value, subtext, color = 'green' }) => {
  const colors = {
    green: 'bg-green-50 text-green-600',
    blue: 'bg-blue-50 text-blue-600',
    yellow: 'bg-yellow-50 text-yellow-600',
    purple: 'bg-purple-50 text-purple-600',
  };
  
  return (
    <div className="bg-white rounded-xl p-4 shadow-soft">
      <div className={`w-10 h-10 ${colors[color]} rounded-lg flex items-center justify-center mb-3`}>
        {icon}
      </div>
      <p className="text-2xl font-bold text-forest">{value}</p>
      <p className="text-xs text-stone">{label}</p>
      {subtext && <p className="text-xs text-stone/60 mt-1">{subtext}</p>}
    </div>
  );
};

const RetentionCard = ({ label, value, benchmark }) => {
  const isGood = value >= benchmark;
  
  return (
    <div className="text-center">
      <p className={`text-4xl font-bold ${isGood ? 'text-green-600' : 'text-orange-500'}`}>
        {value}%
      </p>
      <p className="text-sm font-medium text-forest mt-1">{label} Retention</p>
      <p className="text-xs text-stone/60">Benchmark: {benchmark}%</p>
    </div>
  );
};

const FunnelStep = ({ label, value, percent, highlight = false }) => (
  <div className="relative">
    <div className="flex justify-between items-center mb-1">
      <span className={`text-sm ${highlight ? 'font-medium text-forest' : 'text-stone'}`}>{label}</span>
      <span className="text-sm font-medium text-forest">{value}</span>
    </div>
    <div className="h-2 bg-forest/10 rounded-full overflow-hidden">
      <div 
        className={`h-full ${highlight ? 'bg-lime' : 'bg-forest/30'} rounded-full transition-all duration-500`}
        style={{ width: `${percent}%` }}
      />
    </div>
    <span className="text-xs text-stone/60">{percent}%</span>
  </div>
);

export default AnalyticsDashboard;
